import { useFormContext } from "react-hook-form";
import { useContext, useEffect } from "react";
import { classes } from "../../../../../util/style-utils";
import { GeaoContext } from "../../../GeaoContextProvider";
import RadioButtonGroup from "../../../../gds/RadioButtonGroup";
import YesNoOptions from "../../../../../constants/options/YesNoOptions";
import SelectBox from "../../../../gds/SelectBox";
import CaptiveCarrierOptions from "../../../constants/options/CaptiveCarrierOptions";

/**
 * CaptiveAgentQuestions component renders form fields for collecting information
 * about whether an agent is currently a captive agent with any insurance carrier.
 * 
 * This component handles:
 * - Yes/No question about being a captive agent
 * - Carrier selection dropdown (when user answers "yes")
 * - Custom carrier input field (when "other" is selected)
 * - Dynamic field clearing based on user selections
 * - Initial data mapping from agent interview context
 * 
 * @component
 * @returns {JSX.Element} The rendered captive agent questions form section
 * 
 * @example
 * // Used within a form context (react-hook-form)
 * <FormProvider methods={methods}>
 *   <CaptiveAgentQuestions />
 * </FormProvider>
 */
export default function CaptiveAgentQuestions() {
    /** @type {Object} Context containing agent interview data */
    const { agentInterview } = useContext(GeaoContext);

    /**
     * React Hook Form utilities for form management
     * @type {Object}
     */
    const {
        register,
        control,
        watch,
        setValue,
        formState: { errors }
    } = useFormContext();

    /** @type {string|undefined} Current value of the captive agent field */
    const watchCaptiveAgent = watch("captiveAgent");

    /** @type {string|undefined} Current value of the captive carrier field */
    const watchCaptiveCarrier = watch("captiveCarrier");

    /**
     * Effect to handle initial data mapping for captive carrier options.
     * Maps existing captive carrier data from agentInterview context to form fields.
     * If the carrier is not in the predefined options, sets it as "other" and 
     * populates the custom input field.
     */
    useEffect(() => {
        const captiveCarrier = agentInterview?.questionAnswers?.CaptiveCarrier?.answer;

        if (captiveCarrier && !CaptiveCarrierOptions.some(o => o.value === captiveCarrier)) {
            setValue("captiveCarrierOther", captiveCarrier);
            setValue("captiveCarrier", "other");
        } else if (captiveCarrier) {
            setValue("captiveCarrier", captiveCarrier);
        }
    }, [agentInterview, setValue]);

    /**
     * Effect to clear captive carrier fields when user selects "No" for captive agent.
     * This ensures form consistency by removing carrier-related data when not applicable.
     */
    useEffect(() => {
        if (watchCaptiveAgent === "false") {
            setValue("captiveCarrier", "");
            setValue("captiveCarrierOther", "");
        }
    }, [watchCaptiveAgent, setValue]);

    return (
        <>
            <RadioButtonGroup
                control={control}
                name="captiveAgent"
                legend={<>
                    Are you currently a captive agent with any insurance carrier?{/*
                    */}<span className='required'>*</span>
                </>}
                layout="Two Column"
                options={YesNoOptions}
            />

            {watchCaptiveAgent === "true" &&
                <div className="flex-row">
                    <div className="component-field">
                        <SelectBox
                            control={control}
                            name="captiveCarrier"
                            options={CaptiveCarrierOptions}
                            placeholder="Please Select"
                            label={<>
                                For which carrier are you a captive agent?{/*
                                */}<span className='required'>*</span>
                            </>}
                        />
                    </div>

                    {watchCaptiveCarrier === "other" &&
                        <div className={classes("form-field", errors.captiveCarrierOther && "form-field--error")}>
                            <label htmlFor="captiveCarrierOther" className="text">
                                Please Specify{/*
                                */}<span className='required'>*</span>
                            </label>
                            <input type="text" id="captiveCarrierOther" {...register("captiveCarrierOther")} />
                            {errors.captiveCarrierOther &&
                                <span className="form-message">{errors.captiveCarrierOther.message}</span>
                            }
                        </div>
                    }
                </div>
            }
        </>
    );
}